'use client'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, RotateCcw, X } from 'lucide-react'
import { MultiDropzone } from '@/components/patterns/multi-dropzone'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  OnboardingLayout,
  uploadDocumentSchema,
  type UploadDocumentFormValues,
} from '@/features/onboarding'
import { MAX_DOCUMENT_BYTES, SERVICE_TYPE_LABEL } from '@/features/cases'
import {
  usePublicUploadLinkInfo,
  useUploadPublicDocument,
} from '@/features/public/hooks/use-public'

const KIND_OPTIONS = [
  { value: 'cas_pdf', label: 'CAS Statement (CDSL / NSDL / CAMS / KFintech)' },
  { value: 'broker_xlsx', label: 'Portfolio / Trade file (Excel)' },
] as const

type DocumentKind = UploadDocumentFormValues['kind']
type QueueStatus = 'pending' | 'uploading' | 'done' | 'error'

interface QueuedFile {
  id: string
  file: File
  kind: DocumentKind
  password: string
  status: QueueStatus
  progress: number
  error?: string
  /** false for a file that failed a client-side pre-check (currently: too large) — retrying
   * would just fail again immediately, so those rows only offer Remove, not Retry. */
  retryable: boolean
}

const MAX_QUEUE_SIZE = 20

function formatSize(bytes: number) {
  const kb = bytes / 1024
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`
}

function formatExpiry(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function rowPasswordError(row: Pick<QueuedFile, 'kind' | 'password'>): string | undefined {
  const parsed = uploadDocumentSchema.safeParse({
    kind: row.kind,
    password: row.password || undefined,
  })
  return parsed.success ? undefined : parsed.error.issues[0]?.message
}

/**
 * The public, token-driven upload page — reuses `uploadDocumentSchema`/`useUploadPublicDocument`
 * from the onboarding funnel/Phase 11a unchanged. Supports queuing and uploading several documents
 * in one visit (drag/drop or multi-select), instead of one at a time: files are added to a preview
 * queue immediately, then uploaded sequentially — one `useFileUpload` XHR in flight at a time — when
 * "Upload all" is clicked, with each row tracking its own progress/status independently. The
 * backend's own upload links are reusable (not single-use, no cap on document count), so this is a
 * pure frontend change.
 */
export function UploadTokenView({ token }: { token: string }) {
  const linkInfo = usePublicUploadLinkInfo(token)
  const upload = useUploadPublicDocument(token)

  const [defaultKind, setDefaultKind] = useState<DocumentKind>('cas_pdf')
  const [queue, setQueue] = useState<QueuedFile[]>([])
  const [capMessage, setCapMessage] = useState<string | null>(null)
  const [isProcessingQueue, setIsProcessingQueue] = useState(false)
  const [processingId, setProcessingId] = useState<string | null>(null)
  // Shown for an empty queue, and reopened via "Upload more" once every queued file is Done —
  // hidden the rest of the time so the picker doesn't compete with the queue mid-batch.
  const [showPicker, setShowPicker] = useState(true)

  // The single source of truth `processQueue`'s loop reads from — kept in lockstep with `queue`
  // by every mutator below (not via a `useEffect`, which only flushes on React's own render/commit
  // schedule and would leave this stale mid-loop across consecutive awaited iterations, causing the
  // loop to keep re-selecting the same already-finished row forever).
  const queueRef = useRef<QueuedFile[]>([])

  function setBoth(next: QueuedFile[]) {
    queueRef.current = next
    setQueue(next)
  }

  useEffect(() => {
    if (queue.length === 0) setShowPicker(true)
  }, [queue.length])

  function updateRow(id: string, patch: Partial<QueuedFile>) {
    setBoth(queueRef.current.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }

  // Mirrors the single shared upload hook's progress onto whichever row is currently in flight.
  // `updateRow` is a plain, freshly-defined-per-render function, not itself a meaningful dependency
  // of "when should this effect re-run" — only `upload.progress`/`processingId` should trigger it.
  useEffect(() => {
    if (!processingId) return
    updateRow(processingId, { progress: upload.progress })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload.progress, processingId])

  function removeRow(id: string) {
    setBoth(queueRef.current.filter((r) => r.id !== id))
  }

  function handleFilesAdded(files: File[]) {
    const remaining = MAX_QUEUE_SIZE - queueRef.current.length
    const accepted = files.slice(0, Math.max(remaining, 0))
    setCapMessage(
      files.length > accepted.length
        ? `You can queue up to ${MAX_QUEUE_SIZE} files at a time.`
        : null,
    )
    if (accepted.length === 0) return

    const newItems: QueuedFile[] = accepted.map((file) => {
      const oversized = file.size > MAX_DOCUMENT_BYTES
      return {
        id: crypto.randomUUID(),
        file,
        kind: defaultKind,
        password: '',
        status: oversized ? 'error' : 'pending',
        progress: 0,
        error: oversized ? 'File is larger than 20 MB.' : undefined,
        retryable: !oversized,
      }
    })
    setBoth([...queueRef.current, ...newItems])
    setShowPicker(false)
  }

  // Only ever advances through 'pending' rows — a row that just failed stays 'error' until its own
  // Retry button explicitly resets it back to 'pending' (see the Retry button below), so one pass
  // over the batch can't get stuck re-attempting the same failing file before reaching the rest.
  async function processQueue() {
    if (isProcessingQueue) return
    setIsProcessingQueue(true)
    try {
      for (;;) {
        const next = queueRef.current.find((r) => r.status === 'pending')
        if (!next) break

        setProcessingId(next.id)
        updateRow(next.id, { status: 'uploading', progress: 0, error: undefined })
        try {
          await upload.mutateAsync({
            file: next.file,
            kind: next.kind,
            password: next.password || undefined,
          })
          updateRow(next.id, { status: 'done', progress: 100 })
        } catch (err) {
          updateRow(next.id, {
            status: 'error',
            retryable: true,
            error:
              err instanceof Error ? err.message : 'Something went wrong uploading this document.',
          })
        }
      }
    } finally {
      setProcessingId(null)
      setIsProcessingQueue(false)
    }
  }

  if (linkInfo.isLoading || !linkInfo.data) {
    return (
      <OnboardingLayout title="Upload your documents" fitContent>
        <Skeleton className="h-40 w-full" />
      </OnboardingLayout>
    )
  }

  // Only true 'pending' rows drive "Upload all" — a failed row gets its own Retry button instead
  // (see `processQueue`'s doc comment for why the two are kept separate).
  const pendingRows = queue.filter((r) => r.status === 'pending')
  const hasInvalidPending = pendingRows.some((r) => !!rowPasswordError(r))
  const canUploadAll = pendingRows.length > 0 && !hasInvalidPending && !isProcessingQueue
  const allDone = queue.length > 0 && queue.every((r) => r.status === 'done')

  return (
    <OnboardingLayout
      title="Upload your documents"
      description={`For your ${SERVICE_TYPE_LABEL[linkInfo.data.serviceType]} request — link expires ${formatExpiry(linkInfo.data.expiresAt)}.`}
    >
      <div className="flex flex-col gap-3">
        {showPicker && (
          <>
            <Field>
              <FieldLabel htmlFor="upload-kind">Document type</FieldLabel>
              <Select value={defaultKind} onValueChange={(v) => setDefaultKind(v as DocumentKind)}>
                <SelectTrigger id="upload-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <MultiDropzone
              accept={defaultKind === 'cas_pdf' ? '.pdf' : '.xlsx,.xls'}
              helpText={
                defaultKind === 'cas_pdf'
                  ? '.pdf up to 20 MB each — drop several at once'
                  : '.xlsx/.xls up to 20 MB each — drop several at once'
              }
              onFilesAdded={handleFilesAdded}
            />
          </>
        )}

        {/* Rendered outside the `showPicker` block on purpose — adding files that overflow the
            cap hides the picker in the same update that sets this message, so it would never be
            visible if it lived inside that block. */}
        {capMessage && <p className="text-muted-foreground text-xs">{capMessage}</p>}

        {queue.length > 0 && (
          <ul className="flex flex-col gap-2.5">
            {queue.map((row) => {
              const passwordError = rowPasswordError(row)
              return (
                <li key={row.id} className="rounded-lg border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{row.file.name}</p>
                      <p className="text-muted-foreground text-xs">{formatSize(row.file.size)}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {row.status === 'done' && (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle2 className="size-3.5 text-green-600" /> Done
                        </Badge>
                      )}
                      {row.status === 'error' && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertCircle className="size-3.5" /> Error
                        </Badge>
                      )}
                      {row.status !== 'uploading' && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-7"
                          aria-label={`Remove ${row.file.name}`}
                          onClick={() => removeRow(row.id)}
                        >
                          <X className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {row.status === 'pending' && (
                    <div className="mt-2.5 flex flex-col gap-2">
                      <Select
                        value={row.kind}
                        onValueChange={(v) => updateRow(row.id, { kind: v as DocumentKind })}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KIND_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {row.kind === 'cas_pdf' && (
                        <Field>
                          <Input
                            type="text"
                            placeholder="Statement password — PAN, or your KFintech password"
                            aria-invalid={!!passwordError}
                            value={row.password}
                            onChange={(e) => updateRow(row.id, { password: e.target.value })}
                          />
                          <FieldError errors={passwordError ? [{ message: passwordError }] : []} />
                        </Field>
                      )}
                    </div>
                  )}

                  {row.status === 'uploading' && (
                    <Progress value={row.progress} className="mt-2.5 h-1.5" />
                  )}

                  {row.status === 'error' && (
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <p className="text-destructive text-xs">{row.error}</p>
                      {row.retryable && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isProcessingQueue}
                          onClick={() => {
                            updateRow(row.id, { status: 'pending', error: undefined })
                            void processQueue()
                          }}
                        >
                          <RotateCcw className="size-3.5" /> Retry
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {allDone && !showPicker && (
          <Button variant="outline" onClick={() => setShowPicker(true)}>
            Upload more
          </Button>
        )}

        {pendingRows.length > 0 && (
          <Button className="w-full" disabled={!canUploadAll} onClick={() => void processQueue()}>
            {isProcessingQueue ? 'Uploading…' : 'Upload all'}
          </Button>
        )}
      </div>
    </OnboardingLayout>
  )
}
